import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export type MonthType =
  | 'JANUARY'
  | 'FEBRUARY'
  | 'MARCH'
  | 'APRIL'
  | 'MAY'
  | 'JUNE'
  | 'JULY'
  | 'AUGUST'
  | 'SEPTEMBER'
  | 'OCTOBER'
  | 'NOVEMBER'
  | 'DECEMBER'
  | 'NA';

const MONTHS: MonthType[] = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];

const parseDateParts = (value?: string) => {
  if (!value) return null;

  // supports "yyyy-MM-dd" and "dd-MM-yyyy" (and "/" variants)
  const raw = value.trim();
  const parts = raw.split(/[/-]/).map((p) => p.trim());
  if (parts.length !== 3) return null;

  // yyyy-MM-dd
  if (parts[0].length === 4) {
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return { year, month, day };
  }

  // dd-MM-yyyy
  if (parts[2].length === 4) {
    const day = Number(parts[0]);
    const month = Number(parts[1]);
    const year = Number(parts[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return { year, month, day };
  }

  return null;
};

const monthToNumber = (m?: MonthType): number | null => {
  if (!m) return null;
  const idx = MONTHS.indexOf(m);
  if (idx < 0) return null;
  return idx + 1;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

export interface StudentAttendanceItemDto {
  studentId: number;
  day?: number;
  statusId: number;
  staffId?: number;
  remarks?: string;
}

export interface StudentAttendanceBulkRequestDto {
  staffId: number;
  classId: number;
  sessionId: number;
  month?: MonthType;
  year?: number;
  // legacy support
  attendanceDate?: string;
  students: StudentAttendanceItemDto[];
}

export interface StudentAttendanceResponseDto {
  id: number;
  studentId: number;
  studentName: string;
  staffId: number;
  classId: number;
  sessionId: number;
  month: MonthType;
  year: number;
  dayStatusMap?: Record<number, number | null>;
  remarks?: string;
  lastUpdatedTime: string;

  // legacy support (older UI expects these)
  attendanceDate: string;
  statusId?: number;
  statusCode: string;
  statusCodeLabel?: string;
  teacherId?: number;
}

export interface GetStudentAttendanceParams {
  classId: number;
  sessionId: number;
  month?: MonthType;
  year?: number;
  // legacy support
  date?: string;
  startDate?: string;
  endDate?: string;
}

// Backward compatible exports for existing imports
export interface AttendanceRequestDto {
  studentId: number;
  statusId: number;
  remarks?: string;
}

export type BulkAttendanceRequestDto = StudentAttendanceBulkRequestDto;
export type AttendanceResponseDto = StudentAttendanceResponseDto;

export interface AttendanceStatusRequestDto {
  id?: number;
  statusCode: string;
}

export interface AttendanceStatusResponseDto {
  id: number;
  statusCode: string;
}

const API_URL = `${process.env.REACT_APP_API_URL}/student-attendance`;
const ATTENDANCE_STATUS_API_URL = `${process.env.REACT_APP_API_URL}/attendance-status`;

const normalizeAttendanceResponses = (
  data: any[] | undefined | null
): StudentAttendanceResponseDto[] => {
  if (!Array.isArray(data)) return [];
  return data.map((r: any) => {
    const attendanceDate: string =
      typeof r?.attendanceDate === 'string'
        ? r.attendanceDate
        : (() => {
            const monthNum = monthToNumber(r?.month as MonthType | undefined);
            const yearNum = typeof r?.year === 'number' ? r.year : undefined;
            if (!monthNum || !yearNum) return '';
            return `${yearNum}-${pad2(monthNum)}-01`;
          })();

    const statusCode: string =
      typeof r?.statusCode === 'string'
        ? r.statusCode
        : typeof r?.remarks === 'string'
          ? r.remarks
          : '';

    const normalizeLastUpdatedTime = () => {
      // backend may return LocalDateTime as array: [y, m, d, hh, mm, ss, nano]
      if (typeof r?.lastUpdatedTime === 'string') return r.lastUpdatedTime;
      if (Array.isArray(r?.lastUpdatedTime) && r.lastUpdatedTime.length >= 6) {
        const [y, m, d, hh, mm, ss, nano] = r.lastUpdatedTime as number[];
        const ms = typeof nano === 'number' ? Math.floor(nano / 1_000_000) : 0;
        const pad = (n: number, w = 2) => String(n).padStart(w, '0');
        return `${pad(y, 4)}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:${pad(ss)}.${pad(ms, 3)}`;
      }
      return '';
    };

    return {
      ...r,
      attendanceDate,
      statusCode,
      lastUpdatedTime: normalizeLastUpdatedTime(),
    } as StudentAttendanceResponseDto;
  });
};

export const createBulkStudentAttendance = async (
  data: StudentAttendanceBulkRequestDto
): Promise<StudentAttendanceResponseDto[]> => {
  const derivedDate = parseDateParts(data.attendanceDate);
  const month: MonthType | undefined =
    data.month ?? (derivedDate ? MONTHS[Math.max(0, Math.min(11, derivedDate.month - 1))] : undefined);
  const year: number | undefined = data.year ?? derivedDate?.year;
  const dayFromDate: number | undefined = derivedDate?.day;

  const normalized: StudentAttendanceBulkRequestDto = {
    ...data,
    month,
    year,
    students: (data.students || []).map((s) => ({
      ...s,
      staffId: s.staffId ?? (data.staffId as number),
      day: s.day ?? dayFromDate,
      // backend removed remarks; never send it
      remarks: undefined,
    })),
  };

  const response = await axios.post(
    `${API_URL}/bulk`,
    normalized
  );
  return normalizeAttendanceResponses(response.data);
};

export const getStudentAttendance = async (
  params: GetStudentAttendanceParams
): Promise<StudentAttendanceResponseDto[]> => {
  const derived =
    params.month && params.year
      ? { month: params.month, year: params.year }
      : (() => {
          const base = parseDateParts(params.date || params.startDate);
          if (!base) return null;
          return {
            month: MONTHS[Math.max(0, Math.min(11, base.month - 1))],
            year: base.year,
          };
        })();

  const response = await axios.get(API_URL, {
    params: {
      classId: params.classId,
      sessionId: params.sessionId,
      month: derived?.month ?? params.month,
      year: derived?.year ?? params.year,
    },
  });

  return normalizeAttendanceResponses(response.data);
};

export const getAttendanceStatuses = async (): Promise<AttendanceStatusResponseDto[]> => {
  const response = await axios.get(ATTENDANCE_STATUS_API_URL);
  return response.data;
};

export const createAttendanceStatus = async (
  data: AttendanceStatusRequestDto
): Promise<AttendanceStatusResponseDto> => {
  const response = await axios.post(ATTENDANCE_STATUS_API_URL, data);
  return response.data;
};

export function useAttendance(params?: GetStudentAttendanceParams) {
  const queryClient = useQueryClient();

  const bulkAttendanceMutation = useMutation({
    mutationFn: createBulkStudentAttendance,
    onSuccess: (data, variables) => {
      // Invalidate all attendance queries to ensure the UI updates
      queryClient.invalidateQueries({
        queryKey: ["student-attendance"],
      });
    },
  });

  const getAttendanceQuery = useQuery<StudentAttendanceResponseDto[]>({
    queryKey: [
      "student-attendance",
      params?.classId,
      params?.sessionId,
      params?.month,
      params?.year,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () => {
      if (!params) return Promise.resolve([]);
      return getStudentAttendance(params);
    },
    enabled:
      !!params?.classId &&
      !!params?.sessionId &&
      (!!params?.month && !!params?.year) || !!params?.startDate || !!params?.date,
  });

  // Backward-compatible helper: older UI checks attendance existence by date.
  const useAttendanceByDateQuery = (classId?: number, sessionId?: number, date?: string) => {
    return useQuery<StudentAttendanceResponseDto[]>({
      queryKey: ["student-attendance-by-date", classId, sessionId, date],
      queryFn: async () => {
        if (!classId || !sessionId || !date) return Promise.resolve([]);

        const derived = parseDateParts(date);
        const month = derived ? MONTHS[Math.max(0, Math.min(11, derived.month - 1))] : undefined;
        const year = derived?.year;
        const data = await getStudentAttendance({
          classId,
          sessionId,
          month,
          year,
          date,
        });

        // If API returns attendanceDate per record, filter strictly by the requested date.
        if (data?.some((r) => !!r.attendanceDate)) {
          return data.filter((r) => r.attendanceDate === date);
        }
        return data;
      },
      enabled: !!classId && !!sessionId && !!date,
    });
  };

  const attendanceStatusQuery = useQuery<
    AttendanceStatusResponseDto[]
  >({
    queryKey: ["attendance-status"],
    queryFn: getAttendanceStatuses,
    staleTime: 10 * 60 * 1000, // cache for 10 minutes
  });

  const createAttendanceStatusMutation = useMutation({
    mutationFn: createAttendanceStatus,
    onSuccess: () => {
      // refresh status list
      queryClient.invalidateQueries({
        queryKey: ["attendance-status"],
      });
    },
  });

  return {
    bulkAttendanceMutation,
    getAttendanceQuery,
    useAttendanceByDateQuery,
    attendanceStatusQuery,
    createAttendanceStatusMutation,
  };
}